/**
 * Pedagogy Stage — Pedagogical Contract Validation (Stage 3b)
 *
 * PEDAGOGICAL_CONTRACT_SPEC §8b — Runs between Content (Stage 3) and Visual (Stage 4).
 * Issues use domain 'pedagogy' and stage 3 (no new PipelineStage number).
 *
 * Four checks:
 * 1. DOK-tier alignment — DOK level should match tier band expectations (incl. too-high)
 * 2. Distractor misconception tagging — MC distractors should have misconception tags
 * 3. Indicator-answer parity — Visual indicator values must not leak the answer
 * 4. Structured data detection — Flag questions that rely on regex parsing
 *
 * Severity strategy (TIGHTENED):
 * - PEDAGOGY_INDICATOR_LEAKS_ANSWER: blocker (severity 80) — always
 * - PEDAGOGY_DISTRACTORS_UNTAGGED tier ≥ HIGH_STAKES_TIER: blocker (severity 70) — Control C (high-stakes escalation, independent of the FG4 ramp)
 * - PEDAGOGY_REGEX_FALLBACK tier ≥ HIGH_STAKES_TIER: blocker (severity 70) — fragile parsing is dangerous
 * - PEDAGOGY_STRUCTURED_DATA_MISSING tier ≥ HIGH_STAKES_TIER: blocker (severity 70)
 * - DOK_TOO_LOW: coach (severity 55) — strictness now decided per module by the FG4 ramp downstream (applyEnforcementModes), not by a global flag
 * - DOK_TOO_HIGH: coach (severity 35) — informational
 *
 * C3 (hardening-audit): the legacy global `enforcePedagogicalContract` flag
 * has been removed. Blocker-vs-coach for DOK/distractor pedagogy issues is
 * now driven solely by the per-module enforcement ramp (resolvePedagogicalMode
 * → applyEnforcementModes). High-stakes-tier (Control C) escalation is
 * unchanged and remains independent of that ramp.
 *
 * REGISTRY-DRIVEN (Sprint 8): the set of question types that REQUIRE
 * structured operand data (and therefore cannot fall back to regex-parsing
 * the question text) is read from the questionTypeDefinitions registry via
 * `getTypesRequiringStructuredData()`. Adding a new arithmetic / data-first
 * type only needs the `structuredDataField` property set in the registry —
 * pedagogyStage picks it up automatically.
 */

import type { Question } from '../../../content';
import type { ValidationIssue } from '../issueContract';
import { blocker, coach, info, ISSUE_CODES } from '../issueContract';
import { getPolicy, getTierBand } from '../policyEngine';
import { isKnownMisconception } from '../../data/misconceptions/index';
import { MIN_DOK_BY_TIER_BAND, MAX_DOK_BY_TIER_BAND } from '../../tierBands';
import { checkDokFloor as pureCheckDokFloor, checkIndicatorParity as pureCheckIndicatorParity, checkDistractorsUntagged as pureCheckDistractorsUntagged, checkDeadOptionIndex as pureCheckDeadOptionIndex } from '../../checks/pedagogicalChecks';
import { getTypesRequiringStructuredData, getStructuredDataFieldForType } from '../../questionTypeDefinitions';
import { resolveFamilyTypeHints } from '../../rulebooks';
import { runSubValidator } from './runSubValidator';
import { STAGES } from '../stageDefinitions';
import { SEVERITY } from '../severityScale';

// ─── Named tier thresholds (replace inline magic numbers) ────────────────────
//
// These names match how the rest of the governance domain talks about tier
// stakes: HIGH (tier 9+, eind-tier / plus content), MID (tier 7+,
// uitdaging / pre-eind), so severity escalation reads as policy, not magic.

/**
 * Tier ≥ this value is "high-stakes" — failures here become blockers even
 * when the corresponding enforce-flag is off. Applies to:
 *   - PEDAGOGY_DISTRACTORS_UNTAGGED (via shared pure check)
 *   - PEDAGOGY_REGEX_FALLBACK
 *   - PEDAGOGY_STRUCTURED_DATA_MISSING
 *
 * Audit GSR-24 (Governance Refactor 2026 — PR-11): formerly a module-level
 * `const HIGH_STAKES_TIER = 9` literal. Now read from policy so admin
 * overrides and per-profile tuning take effect without code changes.
 * Function form (not const) ensures every consumer reads the *current*
 * policy on each call — `getPolicy()` is cheap and shallow-frozen.
 */
const getHighStakesTier = (): number => getPolicy().qualityThresholds.highStakesTier;

/**
 * Tier ≥ this value is "mid-stakes" — coach issues at this level escalate
 * one severity bucket (MEDIUM/REVIEW) compared to the lower bands.
 * Used by partial-tagging / no-tags-at-all coach branches.
 */
const MID_STAKES_TIER = 7;

// ─── Tier-band → DOK expectations ─────────────────────────────────────────────
// AUDIT v1.4 / Sprint 4 (V2): MIN_DOK_BY_TIER_BAND and MAX_DOK_BY_TIER_BAND
// are now imported from src/governance/tierBands.ts (the SSOT shared with
// practice/logic/pedagogicalGuard.ts) so pipeline-side validation and
// runtime-guard rejection thresholds can never drift apart.

// ─── Helper: detect regex-parsed operands (ColumnArithmetic/LongDivision pattern) ─

const ARITHMETIC_REGEX_PATTERNS = [/(\d+)\s*[+\-×÷]\s*(\d+)/,
// "123 + 456" in question text
/(\d+)\s*:\s*(\d+)/,
// "144 : 12" division notation
/Bereken\s+(\d+)\s*[+\-×÷:]/i // "Bereken 123 +" pattern
];

/**
 * SSOT: types that require structured operand data come from the
 * questionTypeDefinitions registry (`structuredDataField` property), not
 * a hardcoded set. Adding a new arithmetic type only requires that
 * property — this stage picks it up automatically. Computed lazily and
 * cached because the registry is build-time immutable.
 */
let _cachedStructuredDataTypes: readonly string[] | null = null;
function getStructuredDataTypes(): readonly string[] {
  if (_cachedStructuredDataTypes === null) {
    _cachedStructuredDataTypes = getTypesRequiringStructuredData() as readonly string[];
  }
  return _cachedStructuredDataTypes;
}

/**
 * Type-safe narrowing helper — checks whether `obj` is a non-null object
 * with `field` set to a usable numeric value (number or numeric string).
 * Replaces prior `as any` casts when peeking into the
 * `question.data.columnArithmetic` / `question.data.longDivision` shapes.
 */
function hasNumericField(obj: unknown, field: string): boolean {
  if (typeof obj !== 'object' || obj === null) return false;
  const value = (obj as Record<string, unknown>)[field];
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 && !Number.isNaN(Number(trimmed));
  }
  return false;
}
function detectsRegexFallback(question: Question): boolean {
  // Only flag types whose registry definition declares structuredDataField.
  if (!getStructuredDataTypes().includes(question.questionType)) return false;

  // If structured data exists for the type-specific namespace, no fallback needed.
  const data = question.data;
  if (data) {
    const field = getStructuredDataFieldForType(question.questionType);
    if (field === 'columnArithmetic') {
      // New data-first format (PEDAGOGICAL_CONTRACT_SPEC §7) — typed narrowing
      // helpers replace prior `as any` casts so a future schema change will
      // surface at compile time here.
      if (hasNumericField(data.columnArithmetic, 'operand1')) return false;
    } else if (field === 'longDivision') {
      if (hasNumericField(data.longDivision, 'dividend')) return false;
    }
    // Legacy flat format
    if (data.operand1 !== undefined || data.operands !== undefined) return false;
  }

  // Check if question text contains parseable arithmetic
  return ARITHMETIC_REGEX_PATTERNS.some(re => re.test(question.question));
}

// ─── Check 1: DOK-tier alignment ──────────────────────────────────────────────
//
// PHASE 1b: the floor-violation branch (DOK below tier-band minimum) now
// delegates to the shared pure check `checkDokFloor` from
// `governance/checks/pedagogicalChecks.ts`. Missing-DOK and too-high-DOK
// branches stay pipeline-only — the runtime guard cannot act on them.

function checkDokAlignment(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const dok = question.pedagogy?.dok;
  if (dok === undefined) {
    // PHASE 2: missing-DOK detection is owned by the seed stage
    // (SEED_DOK_MISSING — carries the dokAutoTagger heuristic estimate
    // as structured metadata). pedagogyStage only checks alignment of
    // DOK values that ARE defined.
    return issues;
  }

  // Floor check — DELEGATED to shared pure module (Phase 1b SSOT).
  //
  // C3 (hardening-audit): the legacy global `enforcePedagogicalContract` flag
  // — the only thing that ever promoted this to a blocker — has been removed.
  // The single blocker-vs-coach control is now the per-module FG4 ramp applied
  // downstream in `applyEnforcementModes` (a module on 'enforce' keeps the
  // issue; 'warn'/'shadow' degrade it). The stage therefore always emits coach
  // here; the ramp decides strictness. Behaviour is identical to the prior
  // state where the flag was `false`.
  const floorResult = pureCheckDokFloor(question);
  if (!floorResult.passed) {
    const tierBand = getTierBand(question.tier);
    // Reuse the MODULE-AWARE minimum the gate actually decided on
    // (getMinDokForBand via checkDokFloor), not the raw MIN_DOK_BY_TIER_BAND
    // map. For module-aware floors (e.g. TAA expert-tier accepts DOK 2) the
    // raw map overstates the requirement in the displayed message even though
    // the gate logic itself is correct. Fall back to the raw map only if the
    // metadata is somehow absent.
    const minDok = typeof floorResult.metadata?.minDok === 'number' ? floorResult.metadata.minDok as number : MIN_DOK_BY_TIER_BAND[tierBand] ?? 1;
    issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW, `Vraag ${question.id} heeft DOK ${dok}, maar tier-band '${tierBand}' verwacht minimaal DOK ${minDok}.`, {
      questionId: question.id,
      familyId: question.familyId,
      expected: `DOK ≥ ${minDok}`,
      actual: `DOK ${dok}`,
      severity: SEVERITY.REVIEW
    }));
  }

  // Too-high — pipeline-only (no guard equivalent), kept as-is.
  const tierBand = getTierBand(question.tier);
  const maxDok = MAX_DOK_BY_TIER_BAND[tierBand] ?? 4;
  if (dok > maxDok) {
    issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_TOO_HIGH, `Vraag ${question.id} heeft DOK ${dok}, onverwacht hoog voor tier-band '${tierBand}' (maximum DOK ${maxDok}).`, {
      questionId: question.id,
      familyId: question.familyId,
      expected: `DOK ≤ ${maxDok}`,
      actual: `DOK ${dok}`,
      severity: SEVERITY.LOW
    }));
  }
  return issues;
}

// ─── Check 2: Distractor misconception tagging ───────────────────────────────
//
// PHASE 1b: the no-tags-at-all branch consults the shared pure check
// `checkDistractorsUntagged` for its tier ≥ 9 verdict. The pipeline retains
// the enforce-flag-driven blocking for ALL tiers, the partial-tagging coach,
// and the unknown-tag info branch — those are pipeline-only.

function checkDistractorTags(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Only applies to MC questions with options
  if (question.questionType !== 'multiple_choice') return issues;
  if (!question.options || question.options.length < 2) return issues;
  const tags = question.pedagogy?.misconceptionTags;
  const distractorCount = question.options.length - 1; // exclude correct answer

  if (!tags || tags.length === 0) {
    // No misconception tags at all.
    // Shared pure check governs the "high-stakes (tier ≥ 9), no tags → fail"
    // verdict (Control C — highStakesTier escalation, independent of the
    // FG4 ramp). C3 (hardening-audit): the legacy enforce-flag escalation has
    // been removed; only the tier-≥-9 high-stakes verdict blocks here.
    const pureResult = pureCheckDistractorsUntagged(question);
    const shouldBlock = !pureResult.passed;
    const issue = shouldBlock ? blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED, `Vraag ${question.id} heeft ${distractorCount} afleiders zonder misconceptie-tags.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Voeg pedagogy.misconceptionTags toe voor elke afleider.',
      severity: SEVERITY.HIGH
    }) : coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED, `Vraag ${question.id} heeft ${distractorCount} afleiders zonder misconceptie-tags.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Voeg pedagogy.misconceptionTags toe voor elke afleider.',
      severity: question.tier >= MID_STAKES_TIER ? SEVERITY.REVIEW : SEVERITY.NEAR_MEDIUM
    });
    issues.push(issue);
  } else if (tags.length < distractorCount) {
    // Some distractors tagged, but not all — coach for tier ≥ MID_STAKES_TIER, info otherwise
    const isHighTier = question.tier >= MID_STAKES_TIER;
    issues.push(isHighTier ? coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_PARTIAL, `Vraag ${question.id} heeft ${tags.length}/${distractorCount} afleiders getagd met misconcepties.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Tag alle afleiders met een misconceptie voor volledige didactische dekking.',
      severity: SEVERITY.MEDIUM
    }) : info('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_PARTIAL, `Vraag ${question.id} heeft ${tags.length}/${distractorCount} afleiders getagd met misconcepties.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Tag alle afleiders met een misconceptie voor volledige didactische dekking.',
      severity: SEVERITY.MINOR
    }));
  }

  // Validate misconception tag IDs against the central registry
  //
  // SSOT Fase 1 (mei 2026): tier-aware severity escalation so typos and
  // ad-hoc unprefixed IDs surface in the admin UI instead of being silently
  // dropped on the info floor (severity 30). Previously every unknown tag
  // was severity 30 / NEAR_LOW which made the registry effectively
  // optional — exactly the "vocabulary not signal" problem the audit
  // flagged. Now: coach for normal questions, blocker for high-stakes.
  if (tags && tags.length > 0) {
    const unknownTags = tags.filter(t => !isKnownMisconception(t.id));
    if (unknownTags.length > 0) {
      const isHighStakes = question.tier >= getHighStakesTier();
      const message = `Vraag ${question.id} heeft ${unknownTags.length} onbekende misconceptie-tag(s): ${unknownTags.map(t => t.id).join(', ')}. Voeg toe aan misconceptions registry of corrigeer de ID.`;
      const baseMeta = {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Registreer nieuwe misconceptie-IDs in governance/data/misconceptions/<module>.ts of gebruik een bestaande ID. Misconceptie-tags MOETEN module-prefix gebruiken (rek., get., spr., sww., met., tijd., geld., taa.).'
      };
      issues.push(isHighStakes ? blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_MISCONCEPTION_UNKNOWN, message, {
        ...baseMeta,
        severity: SEVERITY.HIGH
      }) : coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_MISCONCEPTION_UNKNOWN, message, {
        ...baseMeta,
        severity: SEVERITY.MEDIUM
      }));
    }
  }
  return issues;
}

// ─── Check 3: Indicator-answer parity (BLOCKER from day 1) ───────────────────
//
// PHASE 1b: fully delegated to the shared pure check. The pipeline only
// wraps the CheckResult into a ValidationIssue (severity 80, blocker).
// The pure check carries rich metadata (matchKind, indicatorValue,
// correctAnswer, etc.) so the wrapping preserves all admin-UI context.

function checkIndicatorParity(question: Question): ValidationIssue[] {
  const result = pureCheckIndicatorParity(question);
  if (result.passed) return [];
  const meta = result.metadata ?? {};
  const aidType = String(meta.aidType ?? 'unknown');
  const indicatorValue = meta.indicatorValue;
  const matchKind = String(meta.matchKind ?? 'exact-string');
  const tolerance = typeof meta.tolerance === 'number' ? meta.tolerance : 0.05;
  const correctAnswer = meta.correctAnswer;
  const expected = matchKind === 'numeric-proximity' ? `Indicator moet >${(tolerance * 100).toFixed(0)}% afwijken van het antwoord` : 'Indicator mag het antwoord niet onthullen';
  const actual = matchKind === 'numeric-proximity' && typeof meta.relDiff === 'number' ? `indicatorValue=${indicatorValue}, correctAnswer=${correctAnswer}, verschil=${(meta.relDiff * 100).toFixed(2)}%` : `indicatorValue="${indicatorValue}" === correctAnswer="${correctAnswer}"`;
  return [blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER, result.reason ?? 'Indicator leaks answer.', {
    questionId: question.id,
    familyId: question.familyId,
    field: `stimulusAids[${aidType}].indicatorValue`,
    expected,
    actual,
    severity: result.severity ?? SEVERITY.CRITICAL
  })];
}

// ─── Check 4: Structured data detection (regex fallback) ─────────────────────

function checkStructuredData(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const tier = question.tier ?? 1;

  // Check explicit flag first
  if (question.pedagogy?.hasStructuredData === false) {
    if (tier >= getHighStakesTier()) {
      issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_STRUCTURED_DATA_MISSING, `Vraag ${question.id} (tier ${tier}) is gemarkeerd als zonder gestructureerde data — renderer moet operanden uit vraagtekst parsen. Blocker voor tier ≥ 9.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Migreer naar data.columnArithmetic of data.longDivision (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: SEVERITY.HIGH
      }));
    } else {
      issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_STRUCTURED_DATA_MISSING, `Vraag ${question.id} is gemarkeerd als zonder gestructureerde data — renderer moet operanden uit vraagtekst parsen.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Migreer naar data.columnArithmetic of data.longDivision (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: SEVERITY.NEAR_REVIEW
      }));
    }
    return issues;
  }

  // Auto-detect regex fallback pattern
  if (detectsRegexFallback(question)) {
    if (tier >= getHighStakesTier()) {
      issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_REGEX_FALLBACK, `Vraag ${question.id} (tier ${tier}, ${question.questionType}) parst operanden uit vraagtekst. Blocker voor tier ≥ 9 — fragiele parsing kan foutieve antwoorden veroorzaken.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg data.columnArithmetic of data.longDivision toe aan de seed (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: SEVERITY.HIGH
      }));
    } else {
      issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_REGEX_FALLBACK, `Vraag ${question.id} (${question.questionType}) lijkt operanden uit vraagtekst te parsen i.p.v. gestructureerde data te gebruiken.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg data.columnArithmetic of data.longDivision toe aan de seed (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: SEVERITY.NEAR_REVIEW
      }));
    }
  }
  return issues;
}

// ─── Check 5: Family-level question-type hint compliance ─────────────────────
//
// Sessie 3 / P1-8 — reads `familyTypeHints` from the rulebook registry and
// emits a coach when the seed's questionType ∈ a matching hint's
// `discouragedTypes`. Always coach (never blocker) so existing seeds remain
// valid while the hint registry grows; severity escalates at high-stakes
// tier so eind-tier (9+) authoring sees a stronger nudge.
//
// The check is intentionally one-issue-per-seed (first matching hint wins)
// to avoid issue-storms when multiple overlapping patterns match.

function checkFamilyTypeHint(question: Question): ValidationIssue[] {
  const familyId = question.familyId;
  if (!familyId) return [];
  const qType = question.questionType;
  if (!qType) return [];
  const hints = resolveFamilyTypeHints(familyId);
  if (hints.length === 0) return [];

  // First matching hint that lists this type as discouraged wins.
  const offendingHint = hints.find(h => h.discouragedTypes?.includes(qType));
  if (!offendingHint) return [];
  const preferredList = offendingHint.preferredTypes?.length ? ` Voorkeur voor familie: ${offendingHint.preferredTypes.join(', ')}.` : '';
  return [coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_FAMILY_TYPE_DISCOURAGED, `Vraagtype '${qType}' is didactisch zwak voor familie '${familyId}' (${offendingHint.moduleId} rulebook). ${offendingHint.note}${preferredList}`, {
    questionId: question.id,
    familyId,
    expected: offendingHint.preferredTypes?.join(' | ') ?? 'rulebook-preferred type',
    actual: qType,
    severity: question.tier >= getHighStakesTier() ? SEVERITY.REVIEW : SEVERITY.LOW,
    metadata: {
      moduleId: offendingHint.moduleId,
      familyIdPattern: offendingHint.familyIdPattern,
      preferredTypes: offendingHint.preferredTypes ?? [],
      discouragedTypes: offendingHint.discouragedTypes ?? [],
      note: offendingHint.note
    }
  })];
}

// ─── Check 6: Dead optionIndex on misconceptionTags (S-2, shadow-mode) ─────
//
// ARCHITECTURAL_PEER_REVIEW_PLAN_2026 §F0.B — shadow-mode signal surfaced
// by REK Q2Q Batch 3. Fires for any non-MC question carrying
// `pedagogy.misconceptionTags[].optionIndex`. Always emitted as `info`
// (never a blocker, never escalates with enforce flag) so it stays a pure
// telemetry signal until the cross-module impact is reduced.

function checkDeadOptionIndexOnNonMc(question: Question): ValidationIssue[] {
  const result = pureCheckDeadOptionIndex(question);
  if (result.passed) return [];
  return [info('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DEAD_OPTION_INDEX, result.reason ?? 'Dead optionIndex on non-MC misconceptionTags.', {
    questionId: question.id,
    familyId: question.familyId,
    field: 'pedagogy.misconceptionTags[].optionIndex',
    severity: result.severity ?? SEVERITY.COSMETIC,
    suggestion: 'Verwijder optionIndex bij misconceptie-tags op niet-MC vraagtypes — het veld wordt niet gerenderd en hoort bij multiple_choice.',
    metadata: result.metadata
  })];
}

// ─── Check 7: EN-language misconception-IDs (SSOT §3, shadow-mode) ──────────
//
// MET Q2Q consolidatie + SSOT §3 amendement (3 juni 2026). Shadow-mode
// signal: a seed uses a misconception-ID whose body (after the module
// prefix) is English. Always emitted as `info` — pure telemetry while the
// per-module counter in MISCONCEPTION_REGISTRY_SEED_SYNC_2026.md §6 is
// being driven to 0. Conforms to ARCHITECTURAL_PEER_REVIEW_PLAN_2026
// §FG4 / §F0.B shadow-mode pattern.
//
// Heuristic: an ID-body is flagged English when it contains at least one
// recognisable English-only stem from a small curated set. We deliberately
// avoid a broad ASCII-only test because Dutch IDs also use ASCII; the stem
// list is the more specific signal. Adding new stems is a one-line edit
// and the function is allowlist-aware for legitimate loanword IDs.

// English-only stems (lowercased, hyphen-padded). A match anywhere in the
// hyphen-padded ID body counts. Curated from the actual EN-ID inventory
// across the existing registry: rek/spr/sww/get/met/geld English IDs as
// of juni 2026.
const EN_LEGACY_STEMS: readonly string[] = [
// Common verbs / connectives that only appear in English IDs
'-confusion', '-confused', '-forgotten', '-instead-', '-wrong-', '-error', '-missing-', '-omitted', '-omission', '-reversed', '-swap', '-swapped', '-mismatch', '-applied', '-misapplied', '-ignored', '-extracted', '-extraction', '-stripped', '-dropped', '-added', '-extra-', '-correct-', '-wrong-',
// English nouns / fragments that signal EN-authored IDs
'-step-undercount', '-step-overcount', '-direction-reversed', '-instead-of-', '-as-final', '-as-answer', '-as-plural', '-without-', '-with-', '-from-',
// Standalone English stems (must match at word boundary via hyphen-padding)
'-volume-', '-area-', '-perimeter-', '-parallelogram-', '-fraction-', '-decimal-', '-percentage-', '-percent-', '-multiplication-', '-division-', '-divisor-', '-dividend-', '-rounding-', '-rounding-', '-carry-', '-borrow-', '-column-', '-brackets-',
// NOTE: '-context-' REMOVED (juni 2026) — it is spelled IDENTICALLY in Dutch
// and English, so it false-positived every legitimate Dutch `*.context-*`
// ID (e.g. rek.context-verkeerde-gegevens, rek.context-stap-vergeten). Those
// are canonical NL IDs that survived the REK migration; flagging them as
// EN-legacy was pure noise (the "26 REK findings" that were all Dutch).
// A genuinely-English context-ID still trips other stems (-wrong-, -missing-,
// -instead-, -reversed-, …), so detection is not weakened for real EN IDs.
'-strategy-', '-calculation-', '-estimation-', '-measure-', '-measurement-', '-length-', '-scale-', '-symmetry-', '-precision-', '-angle-', '-triangle-', '-isosceles-', '-equilateral-', '-kofschip-', '-stem-', '-infinitive-', '-irregular-', '-loanword-', '-latin-plural-', '-greek-', '-linking-',
// '-trema-' and '-accent-' REMOVED (juni 2026): both are spelled identically
// in Dutch ("trema", "accent"), so they false-positived legitimate Dutch
// spelling IDs (spr.*-trema-*, spr.accent-*). Real English variants still
// trip the surrounding action stems.
'-syllable-', '-consonant-', '-vowel-', '-tense-', '-passive-', '-noun-', '-verb-', '-fact-vs-opinion', '-place-value-', '-change-calculation', '-total-vs-unit'
// '-btw-' and '-wissel-' REMOVED (juni 2026): "btw" (BTW) and "wissel" are
// DUTCH, not English. They false-positived Dutch GELD IDs whose genuinely-
// English fragment lives elsewhere in the body and is still caught — e.g.
// geld.btw-add-instead and geld.wissel-divide-instead both still fire via
// the '-instead-' stem. Matching the Dutch noun itself was the bug.
] as const;

// Loanword-style allowlist (SSOT §3 exception clause). IDs that match any
// of these prefixes are LEGITIMATELY English because the misconception is
// expressly about English spelling/grammar — there is no Dutch equivalent.
// Future work (§6.5): promote to a standalone allowlist registry.
const LOANWORD_ALLOWLIST_PREFIXES: readonly string[] = ['spr.loanword-', 'spr.engels-spelling-', 'sww.engels-grammatica-'] as const;
function isLoanwordAllowed(id: string): boolean {
  return LOANWORD_ALLOWLIST_PREFIXES.some(prefix => id.startsWith(prefix));
}
function isEnLegacyId(id: string): {
  matched: boolean;
  stem?: string;
} {
  // Module prefix is everything up to and including the first '.'.
  const dotIdx = id.indexOf('.');
  if (dotIdx < 0) return {
    matched: false
  };
  const body = id.slice(dotIdx + 1).toLowerCase();
  // Hyphen-pad so stems like '-confusion' can match the tail of the ID.
  const padded = `-${body}-`;
  for (const stem of EN_LEGACY_STEMS) {
    if (padded.includes(stem)) return {
      matched: true,
      stem
    };
  }
  return {
    matched: false
  };
}
function checkEnLegacyMisconception(question: Question): ValidationIssue[] {
  const tags = question.pedagogy?.misconceptionTags;
  if (!tags || tags.length === 0) return [];
  const flagged: {
    id: string;
    stem: string;
  }[] = [];
  for (const tag of tags) {
    if (!tag?.id) continue;
    if (isLoanwordAllowed(tag.id)) continue;
    const result = isEnLegacyId(tag.id);
    if (result.matched && result.stem) {
      flagged.push({
        id: tag.id,
        stem: result.stem
      });
    }
  }
  if (flagged.length === 0) return [];
  const idList = flagged.map(f => f.id).join(', ');
  return [info('pedagogy', 3, ISSUE_CODES.PEDAGOGY_MISCONCEPTION_EN_LEGACY, `Vraag ${question.id} gebruikt ${flagged.length} Engelstalige misconceptie-ID(s): ${idList}. Per SSOT §3 zijn misconceptie-IDs Nederlands; Engelstalige IDs zijn legacy en worden gemigreerd.`, {
    questionId: question.id,
    familyId: question.familyId,
    field: 'pedagogy.misconceptionTags[].id',
    severity: SEVERITY.COSMETIC,
    suggestion: 'Hernoem de misconceptie-IDs naar Nederlands (zie MISCONCEPTION_REGISTRY_SEED_SYNC_2026.md §6 voor de per-module migratie-tracker). Uitzondering: pure leenwoord-misconcepties (spr.loanword-*, spr.engels-spelling-*, sww.engels-grammatica-*) blijven toegestaan.',
    metadata: {
      enLegacyIds: flagged.map(f => ({
        id: f.id,
        matchedStem: f.stem
      })),
      totalFlagged: flagged.length
    }
  })];
}

// ─── Stage Entry Point ────────────────────────────────────────────────────────

/**
 * Run the pedagogy stage on a single question.
 *
 * Issues use domain 'pedagogy' and stage 3 (runs as Stage 3b).
 * Blocker-vs-coach for DOK/distractor issues is decided per module by the
 * FG4 enforcement ramp downstream (resolvePedagogicalMode → applyEnforcementModes);
 * the stage itself always emits coach (C3 hardening-audit — the legacy global
 * enforcePedagogicalContract flag was removed). High-stakes-tier escalation
 * (Control C) is independent and unchanged.
 *
 * R7 (Sprint 5): each check is crash-isolated via runSubValidator so one
 * crashing check (e.g. a bad regex in checkStructuredData) cannot blind
 * the other four.
 *
 * Sessie 3 / P1-8: `checkFamilyTypeHint` reads the rulebook's
 * `familyTypeHints` to flag seeds whose questionType is discouraged for
 * the family. Always coach — never blocker — so the registry can grow
 * without breaking existing content.
 */
export function runPedagogyStage(question: Question): ValidationIssue[] {
  return [...runSubValidator('checkDokAlignment', 'pedagogy', STAGES.PEDAGOGY, () => checkDokAlignment(question)), ...runSubValidator('checkDistractorTags', 'pedagogy', STAGES.PEDAGOGY, () => checkDistractorTags(question)), ...runSubValidator('checkIndicatorParity', 'pedagogy', STAGES.PEDAGOGY, () => checkIndicatorParity(question)), ...runSubValidator('checkStructuredData', 'pedagogy', STAGES.PEDAGOGY, () => checkStructuredData(question)), ...runSubValidator('checkFamilyTypeHint', 'pedagogy', STAGES.PEDAGOGY, () => checkFamilyTypeHint(question)), ...runSubValidator('checkDeadOptionIndexOnNonMc', 'pedagogy', STAGES.PEDAGOGY, () => checkDeadOptionIndexOnNonMc(question)), ...runSubValidator('checkEnLegacyMisconception', 'pedagogy', STAGES.PEDAGOGY, () => checkEnLegacyMisconception(question))];
}